import React, { useEffect, useMemo, useRef, useState } from 'react';
import { usePlanetSync, resolveTimeProfile } from '@/components/PlanetSyncProvider';
import { api } from '@/lib/api';

/**
 * World Engine — atmospheric layer with admin-controlled background image,
 * dynamic palette injection, and crossfade transitions between environments.
 */

const TIME_OVERLAYS = {
  sunrise:     'linear-gradient(180deg, rgba(255,200,200,0.10) 0%, rgba(220,180,200,0.20) 50%, rgba(40,30,50,0.65) 100%)',
  morning:     'linear-gradient(180deg, rgba(240,225,210,0.10) 0%, rgba(220,200,220,0.18) 50%, rgba(40,30,50,0.70) 100%)',
  afternoon:   'linear-gradient(180deg, rgba(225,210,230,0.10) 0%, rgba(200,190,220,0.20) 50%, rgba(40,30,50,0.70) 100%)',
  golden_hour: 'linear-gradient(180deg, rgba(255,210,180,0.18) 0%, rgba(230,170,180,0.25) 50%, rgba(40,30,50,0.72) 100%)',
  sunset:      'linear-gradient(180deg, rgba(255,180,180,0.18) 0%, rgba(200,140,170,0.30) 50%, rgba(40,30,50,0.78) 100%)',
  evening:     'linear-gradient(180deg, rgba(180,160,200,0.22) 0%, rgba(120,100,150,0.40) 50%, rgba(30,20,40,0.85) 100%)',
  night:       'linear-gradient(180deg, rgba(80,70,110,0.40) 0%, rgba(40,30,60,0.70) 50%, rgba(20,15,30,0.95) 100%)',
};

function applyPalette(p) {
  if (!p) return;
  const root = document.documentElement;
  root.style.setProperty('--pg-primary', p.primary);
  root.style.setProperty('--pg-secondary', p.secondary);
  root.style.setProperty('--pg-tertiary', p.tertiary);
  root.style.setProperty('--pg-accent', p.accent);
  root.style.setProperty('--pg-deep', p.deep);
  root.style.setProperty('--pg-ink', p.ink || '#FBF6EE');
  root.style.setProperty('--pg-ink-contrast', p.ink_contrast || '#2A1F26');
}

function Clouds({ intensity = 0.4, motion = true }) {
  const drift = motion ? `pg-drift ${(80 / Math.max(0.2, intensity)).toFixed(0)}s linear infinite` : 'none';
  return (
    <svg viewBox="0 0 1600 400" preserveAspectRatio="xMidYMid slice" className="absolute inset-x-0 top-0 w-[200%] h-[35vh] opacity-20 pointer-events-none" style={{ animation: drift }} aria-hidden>
      <defs>
        <filter id="pg-blur2"><feGaussianBlur stdDeviation="26" /></filter>
        <radialGradient id="pg-cloud2" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="white" stopOpacity="0.45" />
          <stop offset="100%" stopColor="white" stopOpacity="0" />
        </radialGradient>
      </defs>
      <g filter="url(#pg-blur2)" fill="url(#pg-cloud2)">
        <ellipse cx="160" cy="120" rx="220" ry="44" />
        <ellipse cx="540" cy="180" rx="280" ry="60" />
        <ellipse cx="960" cy="100" rx="240" ry="46" />
        <ellipse cx="1320" cy="160" rx="300" ry="58" />
        <ellipse cx="1760" cy="120" rx="220" ry="44" />
      </g>
    </svg>
  );
}

function Stars({ count = 70 }) {
  const stars = useMemo(() => Array.from({ length: count }, () => ({ cx: Math.random()*100, cy: Math.random()*55, r: Math.random()*1.2+0.3, delay: Math.random()*4 })), [count]);
  return (
    <svg viewBox="0 0 100 60" preserveAspectRatio="none" className="absolute inset-0 w-full h-[55vh] pointer-events-none opacity-60" aria-hidden>
      {stars.map((s, i) => (
        <circle key={i} cx={s.cx} cy={s.cy} r={s.r} fill="#fff" opacity="0.6">
          <animate attributeName="opacity" values="0.2;0.85;0.2" dur={`${3+s.delay}s`} repeatCount="indefinite" />
        </circle>
      ))}
    </svg>
  );
}

export default function WorldEngine() {
  const { state } = usePlanetSync();
  const [environments, setEnvironments] = useState([]);
  const [layerA, setLayerA] = useState(null); // currently showing image
  const [layerB, setLayerB] = useState(null); // incoming image
  const [activeLayer, setActiveLayer] = useState('A');
  const rotationIndex = useRef(0);
  const rotationTimer = useRef(null);

  // Fetch environments for auto rotation
  useEffect(() => {
    api.get('/environments').then(({ data }) => setEnvironments(data)).catch(() => {});
  }, []);

  // Apply palette globally whenever it changes
  useEffect(() => { applyPalette(state.palette); }, [state.palette]);

  // Background image source resolution
  const initialBg = state.background_image_url;

  // Initialize layer A on first load
  useEffect(() => {
    if (initialBg && !layerA && !layerB) {
      setLayerA(initialBg);
    }
  }, [initialBg, layerA, layerB]);

  // Swap layers when background_image_url changes (manual mode change)
  useEffect(() => {
    if (!initialBg) return;
    const current = activeLayer === 'A' ? layerA : layerB;
    if (current === initialBg) return;
    // crossfade to incoming
    if (activeLayer === 'A') { setLayerB(initialBg); setTimeout(() => setActiveLayer('B'), 50); }
    else { setLayerA(initialBg); setTimeout(() => setActiveLayer('A'), 50); }
  }, [initialBg]); // eslint-disable-line

  // Auto rotation
  useEffect(() => {
    if (rotationTimer.current) clearInterval(rotationTimer.current);
    if (state.background_mode !== 'auto_rotate' || environments.length < 2) return;
    const minutes = Math.max(1, state.rotation_interval_minutes || 12);
    rotationTimer.current = setInterval(() => {
      rotationIndex.current = (rotationIndex.current + 1) % environments.length;
      const env = environments[rotationIndex.current];
      if (!env) return;
      // crossfade
      if (activeLayer === 'A') { setLayerB(env.image_url); setTimeout(() => { setActiveLayer('B'); applyPalette(env.palette); }, 50); }
      else { setLayerA(env.image_url); setTimeout(() => { setActiveLayer('A'); applyPalette(env.palette); }, 50); }
    }, minutes * 60 * 1000);
    return () => clearInterval(rotationTimer.current);
  }, [state.background_mode, state.rotation_interval_minutes, environments, activeLayer]);

  if (!state?.enabled) return null;

  const time = resolveTimeProfile(state.time_profile);
  // Full 0-1 range for user's overlay_strength setting (no hidden clamp).
  const overlayStrength = Math.min(1.0, Math.max(0, state.overlay_strength ?? 0.55));
  // Readability floor: minimum darkness layer that ALWAYS sits above the
  // background image so the cream --pg-ink text stays legible even on a
  // fully bright/white background — regardless of overlay_strength.
  const READABILITY_FLOOR = 0.55;
  const transitionMs = Math.max(400, state.transition_duration_ms || 1400);
  const isNight = time === 'night' || time === 'evening';
  const motion = state.motion_enabled !== false;
  const intensity = motion ? Math.max(0.15, state.animation_intensity ?? 0.45) : 0;

  const layerStyle = (img, visible) => ({
    backgroundImage: img ? `url('${img}')` : 'none',
    opacity: visible ? 1 : 0,
    transition: `opacity ${transitionMs}ms ease-in-out`,
  });

  return (
    <div aria-hidden className="fixed inset-0 pointer-events-none z-0 overflow-hidden" data-testid="world-engine">
      {/* Two background layers for crossfade */}
      <div className="absolute inset-0 bg-cover bg-center" style={layerStyle(layerA, activeLayer === 'A')} />
      <div className="absolute inset-0 bg-cover bg-center" style={layerStyle(layerB, activeLayer === 'B')} />

      {/* Readability floor — always-on minimum dark layer above the image so
          cream --pg-ink text stays readable on any background (including
          fully bright or white). Sits below the atmospheric time overlay. */}
      <div className="absolute inset-0" style={{
        background: `rgba(10,8,16,${READABILITY_FLOOR})`,
        pointerEvents: 'none',
      }} />

      {/* Atmospheric overlay (time + readability) */}
      <div className="absolute inset-0" style={{
        background: TIME_OVERLAYS[time] || TIME_OVERLAYS.evening,
        opacity: overlayStrength + 0.05,
        transition: 'background 1.6s ease, opacity 1.6s ease',
      }} />

      {/* Soft accent tint from current palette (very subtle, painterly) */}
      <div className="absolute inset-0" style={{
        background: `radial-gradient(60% 60% at 50% 30%, color-mix(in srgb, var(--pg-secondary) 15%, transparent), transparent 60%)`,
        mixBlendMode: 'overlay',
        opacity: 0.5,
      }} />

      {/* Optional weather */}
      {motion && (state.weather === 'partly_cloudy' || state.weather === 'overcast') && <Clouds intensity={intensity} motion />}

      {/* Stars at night */}
      {isNight && <Stars count={80} />}

      {/* Bottom vignette for legibility */}
      <div className="absolute inset-x-0 bottom-0 h-[50vh]" style={{
        background: 'linear-gradient(180deg, transparent 0%, rgba(15,10,22,0.45) 65%, rgba(15,10,22,0.6) 100%)',
      }} />
    </div>
  );
}
